import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import useAppModernisation from "../../hooks/useAppModernisation";
import { IDB_KEY_LENGTH } from '../../constants/ccms';
import classNames from "classnames";
import styles from "./DownloadProgressIcon.module.scss";

// ============================================================================
// DOWNLOAD PROGRESS ICON - 6-bar horizontal progress display
// ============================================================================
// Displays download and IDB extraction progress with 6 stacked horizontal bars:
// - Top 3 bars: Zip download progress (fetch from server or cache)
// - Bottom 3 bars: IDB extraction progress (unzip and store in IndexedDB)
// - Colors: Pink (content), Green (images), Blue (PDFs)
// - Shows checkmark icon when complete, download arrow when in progress
// - Tooltip displays all 6 percentages on hover
// ============================================================================

function ProgressBar({ progress, color }) {
  return (
    <div style={{ marginBottom: "1px" }}>
      <div style={{
        width: "100%",
        backgroundColor: "#e5e7eb",
        height: "6px",
        borderRadius: "3px",
        overflow: "hidden",
        position: "relative"
      }}>
        <div style={{
          width: `${progress}%`,
          backgroundColor: color,
          height: "100%",
          borderRadius: "3px",
          transition: "width 0.3s ease",
          minWidth: progress > 0 ? "2px" : "0"
        }} />
      </div>
    </div>
  );
}

export default function DownloadProgressIcon({zipProgress, idbProgress}) {
  const authID = useSelector((state) => state.counter.id);
  const [downloadCompleted, setDownloadCompleted] = useState(false);
  const { isModernView } = useAppModernisation();
  const iDB = useSelector((state) => state.counter.iDB);

  useEffect(() => {
    const fetchKeys = async () => {
      const iDBKeys = await iDB.keys();
      const downloadCompleted = iDBKeys.length > IDB_KEY_LENGTH;
      setDownloadCompleted(downloadCompleted);
    };
    fetchKeys();
  }, [iDB]);

  if (!downloadCompleted) {
    console.log("DownloadProgressIcon  ZipProgress", zipProgress);
    console.log("DownloadProgressIcon  idbProgress", idbProgress);
    console.log("Progress values:", {
      zipContents: zipProgress?.contentsReceived,
      zipImages: zipProgress?.imagesReceived,
      zipPdfs: zipProgress?.pdfsReceived,
      contents: idbProgress?.contentsReceived,
      images: idbProgress?.imagesReceived,
      pdfs: idbProgress?.pdfsReceived
    });
  }

  if (!(authID?.u || authID?.i)) return null;

  const zipContents = zipProgress?.contentsReceived || 0;
  const zipImages = zipProgress?.imagesReceived || 0;
  const zipPdfs = zipProgress?.pdfsReceived || 0;
  const contents = idbProgress?.contentsReceived || 0;
  const images = idbProgress?.imagesReceived || 0;
  const pdfs = idbProgress?.pdfsReceived || 0;
  const completedColorStyle = downloadCompleted ? styles.completed : styles.pending;
  const modernFontFamilyStyle = !isModernView ? styles.legacyFont : "";

  const isComplete = downloadCompleted || (contents === 100 && images === 100 && pdfs === 100);
  const fillColor = isComplete ? "#10b981" : "#3b82f6";
  if(isComplete) {
      return null;
  }
  return (
    <div
      className={styles.container}
      title={`Zip: Content ${zipContents}% | Images ${zipImages}% | PDFs ${zipPdfs}% | IDB: Content ${contents}% | Images ${images}% | PDFs ${pdfs}%`}
    >
      <div
       className={styles.textSvgWrapper}
     >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          style={{
            marginRight: "6px",
            color: fillColor,
          }}
        >
          {isComplete ? (
            <path
              d="M5 13l4 4L19 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          ) : (
            <path
              d="M12 4v9m0 0l-4-4m4 4 4-4m-4 4v5M20 20H4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>
        <span className={classNames(completedColorStyle, modernFontFamilyStyle)}>
          {isComplete ? "Available Offline" : "Preparing Offline"}
        </span>
      </div>

      {!downloadCompleted && (
        <div>
          <ProgressBar progress={zipContents} color="#f163c6ff" />
          <ProgressBar progress={zipImages} color="#86b910ff" />
          <ProgressBar progress={zipPdfs} color="#3bbbf6ff" />
          <ProgressBar progress={contents} color="#f163c6ff" />
          <ProgressBar progress={images} color="#86b910ff" />
          <ProgressBar progress={pdfs} color="#3bbbf6ff" />
        </div>
       )} 
    </div>
  );
}
